import { useState, useMemo, useEffect, useRef } from 'react';
import { INITIAL_WIZARD_DATA } from '../../components/bids/constants/bidWizardConstants';
import { getBaselineServices, engagementToServices } from '../../components/bids/utils/bidWizardHelpers';
import { BILL_RATES, PRICING_CONTEXT, CLIENT_INTELLIGENCE } from '../../components/bids/constants/mockBidData';

/**
 * Normalize services from the backend.
 * Saved services may have `category` (Step4 shape) or `name` (old shape).
 * Ensures both fields are present so Step4 rendering and calculations both work.
 */
const normalizeServices = (services) => {
  if (!Array.isArray(services)) return [];
  return services.map((svc) => ({
    ...svc,
    category: svc.category || svc.name     || 'Services',
    name:     svc.name     || svc.category || 'Services',
    items: Array.isArray(svc.items)
      ? svc.items.map((item) => ({
          role:      item.role               || 'Acct Mgr',
          hours:     Number(item.hours)      || 0,
          minHours:  item.minHours != null   ? Number(item.minHours)  : undefined,
          maxHours:  item.maxHours != null   ? Number(item.maxHours)  : undefined,
          autoRange: item.autoRange          ?? false,
          rate:      item.rate               || undefined,
          notes:     item.notes              || '',
        }))
      : [],
  }));
};

export const useBidWizardState = (initialWizardData = null, initialStep = 1) => {
  // Capture at mount time so effects can safely reference it without stale closures.
  const loadingFromScenario = useRef(!!initialWizardData);

  const [currentStep, setCurrentStep] = useState(
    loadingFromScenario.current ? initialStep : 1
  );

  const [wizardData, setWizardData] = useState(() => {
    if (loadingFromScenario.current && initialWizardData) {
      // Loading a saved scenario — use its data directly, skip localStorage
      return {
        ...INITIAL_WIZARD_DATA,
        ...initialWizardData,
        services: normalizeServices(initialWizardData.services),
        filters: {
          ...INITIAL_WIZARD_DATA.filters,
          ...(initialWizardData.filters || {}),
        },
        bidFactors: {
          ...INITIAL_WIZARD_DATA.bidFactors,
          ...(initialWizardData.bidFactors || {}),
        },
      };
    }
    return INITIAL_WIZARD_DATA;
  });

  // ── Calculations ──────────────────────────────────────────────────
  const calculations = useMemo(() => {
    const serviceBreakdown = wizardData.services.map((service) => {
      const label      = service.category || service.name || 'Services';
      const totalHours = (service.items || []).reduce((sum, item) => sum + (Number(item.hours) || 0), 0);
      const totalCost  = (service.items || []).reduce(
        (sum, item) => sum + (Number(item.hours) || 0) * (BILL_RATES[item.role] || 0),
        0
      );
      return {
        name:            label,
        category:        label,
        totalHours,
        totalCost,
        avgMonthlyHours: totalHours / 12,
        monthlyFee:      totalCost / 12,
        percentage:      0,
      };
    });

    const grandTotal = serviceBreakdown.reduce((sum, s) => sum + s.totalCost,  0);
    const totalHours = serviceBreakdown.reduce((sum, s) => sum + s.totalHours, 0);

    serviceBreakdown.forEach((s) => {
      s.percentage = grandTotal > 0 ? (s.totalCost / grandTotal) * 100 : 0;
    });

    const isRetainer = wizardData.engagementType === 'retainer';

    return {
      serviceBreakdown,
      grandTotal,
      totalHours,
      totalMonthlyHours: totalHours / 12,
      monthlyFee:        grandTotal / 12,
      // Canonical bid value: monthly for retainers, total for projects
      bidValue:  isRetainer ? grandTotal / 12 : grandTotal,
      totalCost: grandTotal,
    };
  }, [wizardData.services, wizardData.engagementType]);

  // ── Pricing context ───────────────────────────────────────────────
  const pricingContext = useMemo(() => {
    const { industry, clientSize } = wizardData.bidFactors;
    if (!industry || !clientSize || !wizardData.componentType) return null;
    const segment  = `${industry}-${clientSize}`;
    const category = wizardData.engagementType === 'retainer' ? 'retainers' : 'projects';
    return PRICING_CONTEXT[category]?.[segment]?.[wizardData.componentType] || null;
  }, [wizardData.bidFactors.industry, wizardData.bidFactors.clientSize, wizardData.componentType, wizardData.engagementType]);

  // ── Client intelligence ───────────────────────────────────────────
  const clientIntelligence = useMemo(() => {
    if (!wizardData.clientName) return null;
    return CLIENT_INTELLIGENCE[wizardData.clientName] || null;
  }, [wizardData.clientName]);

  // ── Smart insights ────────────────────────────────────────────────
  const insights = useMemo(() => {
    if (currentStep !== 4 || wizardData.services.length === 0) return [];

    const result = [];
    const { monthlyFee, totalMonthlyHours, bidValue } = calculations;
    const blendedRate = totalMonthlyHours > 0 ? monthlyFee / totalMonthlyHours : 0;

    if (wizardData.selectedTemplate?.variance > 10) {
      result.push({
        type:    'warning',
        title:   'Template Had Significant Overservice',
        message: `The reference template overserviced by ${wizardData.selectedTemplate.variance.toFixed(1)}%.`,
        action:  'Consider adding buffer hours or clarifying scope',
      });
    }

    if (clientIntelligence?.avgOverUnder > 5) {
      result.push({
        type:    'warning',
        title:   'Client Overservice Pattern',
        message: `${wizardData.clientName} averages ${clientIntelligence.avgOverUnder.toFixed(1)}% overservice.`,
        action:  'Add 10-15% buffer to hour estimates',
      });
    }

    if (blendedRate > 0 && blendedRate < 100) {
      result.push({
        type:    'error',
        title:   'Low Blended Rate',
        message: `Blended rate of $${blendedRate.toFixed(0)}/hr is below recommended minimum.`,
        action:  'Increase rates or adjust staffing mix',
      });
    }

    if (pricingContext) {
      const currentPrice = wizardData.engagementType === 'retainer' ? monthlyFee : calculations.grandTotal;
      if (currentPrice < pricingContext.min) {
        result.push({
          type:    'warning',
          title:   'Below Market Minimum',
          message: `Your price of $${currentPrice.toLocaleString()} is below market minimum.`,
          action:  'Review pricing strategy',
        });
      } else if (currentPrice > pricingContext.max) {
        result.push({
          type:    'warning',
          title:   'Above Market Maximum',
          message: 'Your price exceeds historical maximum.',
          action:  'Prepare strong value justification',
        });
      }
    }

    if (wizardData.selectedTemplate?.dataSource === 'qb_actuals') {
      const actualsHours = wizardData.selectedTemplate?.hours || 0;
      const currentHours = calculations.totalHours;
      const hoursDiffPct = actualsHours > 0 ? ((currentHours - actualsHours) / actualsHours) * 100 : 0;
      if (Math.abs(hoursDiffPct) > 15) {
        result.push({
          type:    'info',
          title:   'Significant Deviation from Actuals',
          message: `Current bid hours (${currentHours}) differ from reference actuals (${Math.round(actualsHours)}) by ${hoursDiffPct > 0 ? '+' : ''}${hoursDiffPct.toFixed(0)}%.`,
          action:  'Review whether scope changes justify the difference',
        });
      }
    }

    if (blendedRate >= 110 && blendedRate <= 130 && result.filter((i) => i.type === 'error' || i.type === 'warning').length === 0) {
      result.push({
        type:    'success',
        title:   'Strong Pricing',
        message: 'Blended rate and pricing align well with firm standards.',
        action:  null,
      });
    }

    return result;
  }, [wizardData, calculations, pricingContext, clientIntelligence, currentStep]);

  // ── Draft auto-save (only when NOT loading a scenario) ───────────
  useEffect(() => {
    if (loadingFromScenario.current) return;
    localStorage.setItem('wizardDraft', JSON.stringify({ wizardData, currentStep }));
  }, [wizardData, currentStep]);

  // ── Draft restore (only when NOT loading a scenario) ─────────────
  // Runs once on mount. Skipped entirely when a scenario was passed in
  // so the saved scenario data is never overwritten by a stale draft.
  useEffect(() => {
    if (loadingFromScenario.current) return;
    try {
      const draft = localStorage.getItem('wizardDraft');
      if (draft) {
        const parsed = JSON.parse(draft);
        const mergedFilters = {
          searchClient:   '',
          industry:       '',
          timePeriod:     'all',
          startDate:      null,
          endDate:        null,
          excludeUnknown: false,
          ...parsed.wizardData?.filters,
        };
        setWizardData({
          ...parsed.wizardData,
          services: normalizeServices(parsed.wizardData?.services),
          filters:  mergedFilters,
        });
        setCurrentStep(parsed.currentStep);
      }
    } catch {
      // Ignore corrupt drafts
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Navigation ────────────────────────────────────────────────────
  const goToStep = (step) => step >= 1 && step <= 5 && setCurrentStep(step);

  const nextStep = () => {
    if (currentStep === 2 && wizardData.useReference === 'scratch') {
      setCurrentStep(4);
    } else if (currentStep < 5) {
      setCurrentStep(currentStep + 1);
    }
  };

  const previousStep = () => {
    if (currentStep === 4 && wizardData.useReference === 'scratch') {
      setCurrentStep(2);
    } else if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1: return !!(wizardData.engagementType && (wizardData.componentType || wizardData.selectedProjects?.length > 0));
      case 2: return wizardData.useReference !== null;
      case 3:
        if (wizardData.useReference === true)  return !!wizardData.selectedTemplate;
        if (wizardData.useReference === false) return !!wizardData.selectedBaseline;
        return true;
      case 4: return true;
      case 5: return true;
      default: return false;
    }
  };

  // ── Wizard actions ────────────────────────────────────────────────

  /**
   * selectEngagementType
   *
   * @param {string}   type                    - 'retainer' | 'project'
   * @param {string}   componentType           - retainer id or project id(s)
   * @param {string}   componentName           - display name
   * @param {Array}    selectedProjects        - project objects (project flow only)
   * @param {string[]} selectedRetainerServices - service category strings (retainer A/B only)
   */
  const selectEngagementType = (type, componentType, componentName, selectedProjects, selectedRetainerServices) => {
    localStorage.removeItem('wizardDraft');
    setWizardData({
      ...wizardData,
      engagementType:           type,
      componentType,
      componentName,
      selectedProjects:         selectedProjects         || [],
      selectedRetainerServices: selectedRetainerServices || [],
    });
  };

  const selectReferenceDecision = (useRef) => {
    if (useRef === false) {
      setWizardData({ ...wizardData, useReference: false });
      setCurrentStep(3);
    } else if (useRef === 'scratch') {
      setWizardData({ ...wizardData, useReference: 'scratch', services: [] });
      setCurrentStep(4);
    } else {
      setWizardData({ ...wizardData, useReference: true });
      setCurrentStep(3);
    }
  };

  const selectEngagementTemplate = (engagement) => {
    setWizardData({
      ...wizardData,
      selectedTemplate: {
        id:            engagement.id,
        clientName:    engagement.qb_customer_name || engagement.engagement_name,
        componentType: wizardData.componentType,
        industry:      engagement.industry,
        status:        'Won',
        monthlyFee:    (engagement.estimated_value || 0) / 12,
        hours:         engagement.estimated_hours,
        profitMargin:  engagement.profit_margin,
        qbCustomerRef:  engagement.qb_customer_ref  || null,
        qbCustomerName: engagement.qb_customer_name || null,
      },
      clientName: '',
      services:   [],    // ← empty, wait for components
      bidFactors: {
        ...wizardData.bidFactors,
        industry: engagement.industry || wizardData.bidFactors.industry,
      },
    });
  };

  const applyActualsToServices = (actualsServices) => {
    if (!actualsServices?.length) return;
    const normalized = normalizeServices(actualsServices);
    setWizardData((prev) => ({
      ...prev,
      services:         normalized,
      selectedTemplate: {
        ...prev.selectedTemplate,
        services:   normalized,
        dataSource: 'qb_actuals',
      },
    }));
  };

  const updateFilter = (filterName, value) => {
    setWizardData((prev) => ({
      ...prev,
      filters: { ...prev.filters, [filterName]: value },
    }));
  };

  return {
    currentStep,
    wizardData,
    setWizardData,
    calculations,
    pricingContext,
    clientIntelligence,
    insights,
    goToStep,
    nextStep,
    previousStep,
    canProceed,
    selectEngagementType,
    selectReferenceDecision,
    selectEngagementTemplate,
    applyActualsToServices,
    updateFilter,
  };
};